/**
 * Bundle names that form part of the slot-platform contract.
 *
 * Every game built on this template ships exactly these bundles, with these
 * exact names. The shell reads them from here and the asset pipeline builds a
 * bundle per `assets-src` folder of the same name, so a downstream game cannot
 * rename one without changing both.
 *
 * Game-side code MAY also reference these constants (e.g.
 * `createGameAudioOverrides()` declares its audio bank with
 * `name: SHELL_BUNDLE_NAMES.audio`) — recommended, but not required. Game
 * code is free to declare additional bundles beyond this contract.
 *
 * See `.claude/docs/bundles-and-scenes.md` for the per-bundle content
 * contract (folder structure + path conventions inside each bundle).
 */
export const SHELL_BUNDLE_NAMES = {
  /** Tiny boot-loader bundle — background + logo. Loaded first to paint the loader UI within hundreds of ms. */
  bootAssets: "boot-assets",
  /**
   * The title's always-loaded core assets; a mandatory bundle (review 2026-10-01 #24). slot-cannon keeps only
   * `logo/logo.webp` there, which nothing reads (the logos come from `boot-assets`); until decision 40 also the shell's
   * served dictionaries.
   */
  sharedCore: "shared-core",
  /** Always-loaded game art + symbols + character + fonts + game-side locale strings. */
  gameSharedVisuals: "game-shared-visuals",
  /** High-tier visual profile bundle — loaded on strong devices/networks. */
  visualsHigh: "game-visuals-high",
  /** Low-tier visual profile bundle — loaded on weak devices/networks. */
  visualsLow: "game-visuals-low",
  /** All `.m4a` audio assets. Bank declarations (`DefaultAudioBank` + game overrides) reference this. */
  audio: "audio-bundle",
  /**
   * Non-essential decorative content meant to load AFTER boot (frame-sequence effects). The name is
   * reserved: no title ships this bundle and nothing loads it yet. (The Cocos loaders it described
   * are gone.)
   */
  deferredContent: "game-deferred-content",
  /** Shell art (control-bar atlas, borders, loading sheet, Roboto) and the shell's served dictionaries (`localization/shell.<locale>`) — built from `packages/game-shell/assets-src` into every title's manifest; a core bundle the boot loads itself. */
  shellUi: "shell-ui",
} as const;
